import { useState, useRef, useCallback, useEffect } from 'react';
import { Popover } from 'foundations-components/transitional/components/Popover';
import { View } from 'foundations-components/transitional/components/View';
import { Flex } from 'foundations-components/components/Flex';
import { Link } from 'foundations-components/transitional/components/Link';
import { Button } from 'foundations-components/transitional/components/Button';
import { getNavLabel } from '../../constants/navLabels';
import { PortalsSideNavIcon } from './PortalsSideNavIcon';
import { useIsPillBorderRadius } from '../../hooks/useIsPillBorderRadius';
import { getIsThemePreview } from '../../utils/metadata';
import styled from 'foundations-theming-specialty/utils';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const TruncatedLabel = styled.span.withConfig({
  displayName: "PortalsSideNavItem__TruncatedLabel"
})({
  display: 'block',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  // Caps the width in the children flyout (which has no bounded parent). Inline
  // rail labels truncate to the available flex width instead — their wrapper
  // sets min-inline-size: 0, which is narrower than this cap.
  maxInlineSize: '200px'
});
const StyledView = styled(View)(({
  theme,
  $isPill
}) => ({
  borderRadius: theme.borderRadius[$isPill ? '0' : '100'],
  transition: 'background-color 150ms',
  '&:hover, &[data-vr-state="hover"]': {
    backgroundColor: theme.color['fill-surface-default-hover']
  }
}));
export function PortalsSideNavItem({
  item,
  history,
  isExpanded = false
}) {
  var _item$label, _item$label2, _item$label3;
  const [open, setOpen] = useState(false);
  const closeTimeout = useRef(undefined);
  const isPill = useIsPillBorderRadius();
  const hasChildren = item.children.length > 0;
  const handleClientNav = useCallback(navItem => e => {
    if (history && navItem.isCurrent && navItem.path) {
      e.preventDefault();
      history.push(navItem.path);
    } else if (getIsThemePreview()) {
      e.preventDefault();
    }
  }, [history]);
  useEffect(() => () => clearTimeout(closeTimeout.current), []);
  const show = useCallback(() => {
    clearTimeout(closeTimeout.current);
    setOpen(true);
  }, []);
  const hide = useCallback(() => {
    closeTimeout.current = setTimeout(() => setOpen(false), 150);
  }, []);
  const popoverBody = hasChildren ? /*#__PURE__*/_jsx(View, {
    as: "span",
    typography: "body-100",
    color: "text-core-default",
    children: /*#__PURE__*/_jsx(View, {
      onMouseEnter: show,
      onMouseLeave: hide,
      padding: "200",
      children: item.children.map(child => {
        var _child$href, _child$label;
        return /*#__PURE__*/_jsx(StyledView, {
          $isPill: isPill,
          as: "div",
          "data-test-id": `portals-side-nav-popover-item-${child.id}`,
          children: /*#__PURE__*/_jsx(Link, {
            href: (_child$href = child.href) !== null && _child$href !== void 0 && _child$href.startsWith('/') ? child.href : `//${child.href}`,
            external: false,
            display: "block",
            paddingBlock: "100",
            paddingInline: "200",
            use: "unstyled",
            onFocus: show,
            onBlur: hide,
            onKeyDown: e => {
              if (e.key === 'Escape') {
                e.preventDefault();
                setOpen(false);
                const trigger = document.querySelector(`[data-nav-trigger-id="${item.id}"]`);
                trigger === null || trigger === void 0 || trigger.focus();
              }
            },
            onClick: handleClientNav(child),
            children: /*#__PURE__*/_jsx(View, {
              as: "span",
              typography: "body-100",
              color: "text-core-default",
              children: /*#__PURE__*/_jsx(TruncatedLabel, {
                children: (_child$label = child.label) !== null && _child$label !== void 0 ? _child$label : getNavLabel(child.id)
              })
            })
          })
        }, child.id);
      })
    })
  }) : /*#__PURE__*/_jsx(View, {
    onMouseEnter: show,
    onMouseLeave: hide,
    paddingBlock: "100",
    paddingInline: "200",
    children: /*#__PURE__*/_jsx(View, {
      as: "span",
      typography: "body-100",
      color: "text-core-default",
      children: (_item$label = item.label) !== null && _item$label !== void 0 ? _item$label : getNavLabel(item.id)
    })
  });
  const iconBorderRadius = isPill ? '0' : '300';
  const triggerBackground = item.isActive || open ? 'fill-accent-neutral-subtle' : undefined;
  const label = (_item$label2 = item.label) !== null && _item$label2 !== void 0 ? _item$label2 : getNavLabel(item.id);
  const ariaLabel = (_item$label3 = item.label) !== null && _item$label3 !== void 0 ? _item$label3 : item.id;
  const labelNode = /*#__PURE__*/_jsx(View, {
    as: "span",
    typography: "body-100",
    color: "text-core-default",
    flexGrow: "1",
    minInlineSize: "0px",
    children: /*#__PURE__*/_jsx(TruncatedLabel, {
      children: label
    })
  });

  // Fixed-size icon slot so the icon occupies the same horizontal position in
  // both the collapsed rail and the expanded rail — expanding only reveals the
  // label to the right, the icon does not move.
  const iconSlot = /*#__PURE__*/_jsx(Flex, {
    inlineSize: "40px",
    blockSize: "40px",
    flexShrink: 0,
    alignItems: "center",
    justifyContent: "center",
    children: /*#__PURE__*/_jsx(PortalsSideNavIcon, {
      name: item.id
    })
  });
  let trigger;
  if (isExpanded) {
    var _item$href;
    trigger = hasChildren ? /*#__PURE__*/_jsx(View, {
      inlineSize: "100%",
      borderRadius: iconBorderRadius,
      backgroundColor: triggerBackground,
      children: /*#__PURE__*/_jsx(Button, {
        use: "unstyled",
        "aria-label": ariaLabel,
        "aria-expanded": open,
        "aria-haspopup": "true",
        paddingInline: "0",
        paddingBlock: "0",
        "data-nav-trigger-id": item.id,
        onClick: () => setOpen(prev => !prev),
        onKeyDown: e => {
          if (e.key === 'Escape' && open) {
            e.preventDefault();
            setOpen(false);
          }
        },
        children: /*#__PURE__*/_jsxs(Flex, {
          alignItems: "center",
          inlineSize: "100%",
          blockSize: "40px",
          paddingInlineEnd: "200",
          children: [iconSlot, labelNode]
        })
      })
    }) : /*#__PURE__*/_jsx(Flex, {
      borderRadius: iconBorderRadius,
      inlineSize: "100%",
      blockSize: "40px",
      backgroundColor: triggerBackground,
      children: /*#__PURE__*/_jsxs(Link, {
        href: (_item$href = item.href) !== null && _item$href !== void 0 && _item$href.startsWith('/') ? item.href : `//${item.href}`,
        use: "unstyled",
        display: "flex",
        inlineSize: "100%",
        blockSize: "100%",
        alignItems: "center",
        paddingInlineEnd: "200",
        onClick: handleClientNav(item),
        children: [iconSlot, labelNode]
      })
    });
  } else {
    var _item$href2;
    trigger = hasChildren ? /*#__PURE__*/_jsx(Button, {
      use: "unstyled",
      "aria-label": ariaLabel,
      "aria-expanded": open,
      "aria-haspopup": "true",
      paddingInline: "0",
      paddingBlock: "0",
      "data-nav-trigger-id": item.id,
      onClick: () => setOpen(prev => !prev),
      onKeyDown: e => {
        if (e.key === 'Escape' && open) {
          e.preventDefault();
          setOpen(false);
        }
      },
      children: /*#__PURE__*/_jsx(Flex, {
        borderRadius: iconBorderRadius,
        inlineSize: "40px",
        blockSize: "40px",
        backgroundColor: triggerBackground,
        children: iconSlot
      })
    }) : /*#__PURE__*/_jsx(Flex, {
      borderRadius: iconBorderRadius,
      inlineSize: "40px",
      blockSize: "40px",
      backgroundColor: triggerBackground,
      children: /*#__PURE__*/_jsx(Link, {
        href: (_item$href2 = item.href) !== null && _item$href2 !== void 0 && _item$href2.startsWith('/') ? item.href : `//${item.href}`,
        use: "unstyled",
        display: "flex",
        inlineSize: "100%",
        blockSize: "100%",
        alignItems: "center",
        justifyContent: "center",
        "aria-label": ariaLabel,
        onClick: handleClientNav(item),
        children: /*#__PURE__*/_jsx(PortalsSideNavIcon, {
          name: item.id
        })
      })
    });
  }

  // Keep the hover flyout for parents in every state (reveals children) and for
  // collapsed leaf items (reveals the label). When expanded, a leaf's label is
  // already visible inline, so the redundant label-only popover is suppressed.
  const showPopover = hasChildren || !isExpanded;
  return /*#__PURE__*/_jsxs(View, {
    "data-test-id": `portals-side-nav-item-${item.id}`,
    position: "relative",
    onMouseEnter: show,
    onMouseLeave: hide,
    children: [showPopover ? /*#__PURE__*/_jsx(Popover, Object.assign({
      open: open,
      onOpenChange: ({
        target: {
          value
        }
      }) => setOpen(value),
      content: popoverBody,
      placement: "right bottom",
      arrowSize: "none",
      closeOnOutsideClick: false
      // This helps us control the single child nav item border radius to prevent
      // it from being too rounded during pill mode. See GlobalPopoverStyles.tsx
    }, !hasChildren && isPill && {
      'data-nav-label-popover': true
    }, {
      children: trigger
    })) : trigger, item.isActive && /*#__PURE__*/_jsx(Flex, {
      position: "absolute",
      insetInlineEnd: "0",
      insetBlockStart: "0",
      insetBlockEnd: "0",
      alignItems: "center",
      children: /*#__PURE__*/_jsx(Flex, {
        inlineSize: "2px",
        blockSize: "22px",
        borderRadius: "full",
        backgroundColor: "fill-accent-neutral-default"
      })
    })]
  });
}